import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import { type DataViewDataType, type ViewMeta } from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';

import { type DataSourceWriteActor } from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import {
  addView,
  deleteRows,
  deleteView,
  duplicateView,
  moveViewTo,
  updateView,
} from './utils/block-utils.js';
import { databaseBlockViewMap } from './views/index.js';

export function rowAdd(
  this: DatabaseSourceRuntime,
  insertPosition: InsertToPosition | number
): string {
  const rowId = this._model.store.workspace.idGenerator();
  return this.rowAddWithId(rowId, insertPosition).rowId;
}

export function getShardedInsertIndex(
  this: DatabaseSourceRuntime,
  insertPosition: InsertToPosition | number
): number {
  if (typeof insertPosition === 'number') {
    return Math.max(0, Math.min(insertPosition, this._shardedRowIds.length));
  }
  if (insertPosition === 'start') return 0;
  if (insertPosition === 'end') return this._shardedRowIds.length;
  const targetIndex = this._shardedRowIds.indexOf(insertPosition.id);
  if (targetIndex < 0) return this._shardedRowIds.length;
  return insertPosition.before ? targetIndex : targetIndex + 1;
}

export function rowDelete(
  this: DatabaseSourceRuntime,
  ids: string[],
  actor: DataSourceWriteActor = 'user'
): void {
  this.assertFileIndexRowMutationAllowed(actor, 'Row deletion');
  if (this.isRowSharded) {
    this.rowDeleteSharded(ids);
    return;
  }
  this.assertNoLocalRootRowMutation('Row deletion');
  this.doc.captureSync();
  for (const id of ids) {
    const block = this.doc.getBlock(id);
    if (block) {
      this.doc.deleteBlock(block.model);
    }
  }
  deleteRows(this._model, ids);
}

export function rowDeleteSharded(
  this: DatabaseSourceRuntime,
  ids: string[]
): void {
  this.deleteShardedRowsFromLocalDocs(ids);
}

export function rowMove(
  this: DatabaseSourceRuntime,
  rowId: string,
  position: InsertToPosition
): void {
  if (this.isRowSharded) {
    this.materializeAllShardedRows();
    const currentIndex = this._shardedRowIds.indexOf(rowId);
    if (currentIndex < 0) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Cannot move missing sharded database row "${rowId}".`
      );
    }
    const targetIndex = this.getShardedInsertIndex(position);
    const adjustedTargetIndex =
      currentIndex < targetIndex ? targetIndex - 1 : targetIndex;
    if (currentIndex === adjustedTargetIndex) return;
    const row = this._shardedRowsById.get(rowId);
    if (!row) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Cannot move unloaded sharded database row "${rowId}".`
      );
    }
    this.deleteShardedRowsFromLocalDocs([rowId]);
    this.insertShardedRowIntoLocalDoc(rowId, row.cells, adjustedTargetIndex);
    return;
  }
  this.assertNoLocalRootRowMutation('Row move');
  const model = this.doc.getModelById(rowId);
  if (model) {
    const index = insertPositionToIndex(position, this._model.children);
    const target = this._model.children[index];
    if (target?.id === rowId) {
      return;
    }
    this.doc.moveBlocks([model], this._model, target);
  }
}

export function viewDataAdd(
  this: DatabaseSourceRuntime,
  viewData: DataViewDataType
): string {
  this.assertFileIndexRootSchemaWithinLimits({
    views: [...this._model.props.views, viewData],
  });
  this.captureStorageSync();
  addView(this._model, viewData);
  return viewData.id;
}

export function viewDataDelete(
  this: DatabaseSourceRuntime,
  viewId: string
): void {
  const views = this.viewDataList$.value;
  const index = views.findIndex(view => view.id === viewId);
  const nextViewId =
    index >= 0
      ? (views[index + 1]?.id ??
        views[index - 1]?.id ??
        views.find(view => view.id !== viewId)?.id)
      : undefined;

  this.captureStorageSync();
  deleteView(this._model, viewId);
  if (index >= 0 && !this._model.props.views.some(view => view.id === viewId)) {
    this.pruneLocalUiStateForDeletedView(viewId, nextViewId);
  }
}

export function viewDataDuplicate(
  this: DatabaseSourceRuntime,
  id: string
): string {
  const source = this._model.props.views.find(view => view.id === id);
  if (source) {
    this.assertFileIndexRootSchemaWithinLimits({
      views: [...this._model.props.views, source],
    });
  }
  this.captureStorageSync();
  return duplicateView(this._model, id);
}

export function viewDataGet(
  this: DatabaseSourceRuntime,
  viewId: string
): DataViewDataType | undefined {
  return this.viewDataList$.value.find(data => data.id === viewId)!;
}

export function viewDataMoveTo(
  this: DatabaseSourceRuntime,
  id: string,
  position: InsertToPosition
): void {
  this.captureStorageSync();
  moveViewTo(this._model, id, position);
}

export function viewDataUpdate<ViewData extends DataViewDataType>(
  this: DatabaseSourceRuntime,
  id: string,
  updater: (data: ViewData) => Partial<ViewData>
): void {
  const current = this._model.props.views.find(view => view.id === id);
  if (current) {
    const patch = updater(current as ViewData);
    this.assertFileIndexRootSchemaWithinLimits({
      views: this._model.props.views.map(view =>
        view.id === id ? { ...view, ...patch } : view
      ),
    });
    this.captureStorageSync();
    updateView(this._model, id, () => patch);
    return;
  }
  this.captureStorageSync();
  updateView(this._model, id, updater);
}

export function viewMetaGet(
  this: DatabaseSourceRuntime,
  type: string
): ViewMeta {
  const view = databaseBlockViewMap[type];
  if (!view) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Unknown view type: ${type}`
    );
  }
  return view;
}

export function viewMetaGetById(
  this: DatabaseSourceRuntime,
  viewId: string
): ViewMeta | undefined {
  const view = this.viewDataGet(viewId);
  if (!view) {
    return;
  }
  return this.viewMetaGet(view.mode);
}
